/**
 * Enroller Admin Service
 * Handles analytics, dashboard, and Metabase-related operations for Enroller Admin
 */

import { apiClient } from '@/services/core/apiClient';
import { graphqlClient } from '@/services/core/graphqlClient';
import { GET_METABASE_RESOURCES } from '@/services/resources/analyticsGquery';

export interface MetabaseTokenResponse {
  success: boolean;
  id: number;
  type: string;
  token: string;
}

export interface MetabaseConfig {
  type: 'question' | 'dashboard';
  id: number;
  user_email: number | string
}

export interface MetabaseResource {
  id: number;
  name: string;
  resource_type: string;
  resource_id: number;
}

export interface MetabaseResourcesResponse {
  resources: MetabaseResource[];
  success: boolean;
  error?: string;
}

export class EnrollerAdminService {
  private userId: string | number | null = null;
  private token: string | null = null;
  private hasuraToken: string | null = null;

  /**
   * Update service context with user information
   */
  updateContext(context: { userId: string | number; token: string; hasuraToken?: string }) {
    this.userId = context.userId;
    this.token = context.token;
    this.hasuraToken = context.hasuraToken || null;
  }

  /**
   * Get list of available Metabase resources (dashboards/questions)
   * @returns List of available Metabase resources
   */
  async getMetabaseResources(): Promise<MetabaseResourcesResponse> {
    if (!this.hasuraToken) {
      console.error('❌ EnrollerAdminService: Hasura token not set');
      return { resources: [], success: false, error: 'Hasura token not set' };
    }

    try {
      const response = await graphqlClient.query(
        GET_METABASE_RESOURCES,
        {},
        this.hasuraToken
      );

      if (!response.success || !response.data) {
        const errorMsg = response.errors?.[0] || 'Failed to fetch Metabase resources';
        console.error('❌ Failed to fetch Metabase resources:', errorMsg);
        return { resources: [], success: false, error: errorMsg };
      }

      const resources = response.data.api_metabase_resource || [];
      console.log('✅ Metabase resources fetched successfully:', resources);
      
      return {
        resources,
        success: true
      };
    } catch (error) {
      console.error('❌ Error fetching Metabase resources:', error);
      return { 
        resources: [], 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get Metabase authentication token for embedding analytics
   * @param config - Metabase resource configuration (type and id)
   * @returns Metabase token response
   */
  async getMetabaseToken(config: MetabaseConfig): Promise<MetabaseTokenResponse | null> {
    if (!this.userId || !this.token) {
      console.error('❌ EnrollerAdminService: User context not set');
      return null;
    }

    try {
      const endpoint = `/api/ngpep/${this.userId}/metabase-token?type=${config.type}&id=${config.id}&user_email=${config.user_email}`;
      const response = await apiClient.get<MetabaseTokenResponse>(endpoint, this.token);

      if (response.success && response.data) {
        console.log('✅ Metabase token fetched successfully:', response.data);
        return response.data;
      } else {
        console.error('❌ Failed to fetch Metabase token:', response.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching Metabase token:', error);
      return null;
    }
  }

  /**
   * Build Metabase embed URL
   * @param resourceType - Type of Metabase resource (question or dashboard)
   * @param token - Metabase authentication token
   * @param baseUrl - Optional base URL (defaults to analytics.hbox.ai)
   * @returns Complete embed URL
   */
  buildMetabaseEmbedUrl(
    resourceType: 'question' | 'dashboard',
    token: string,
    baseUrl: string = 'https://analytics.hbox.ai'
  ): string {
    return `${baseUrl}/embed/${resourceType}/${token}`;
  }
}

// Export singleton instance
export const enrollerAdminService = new EnrollerAdminService();
